import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    resources: {
      en: {
        translation: {
          /* === NAV (5 primary + More + 4 secondary) === */
          nav: {
            home: "Home",
            about: "About",
            experience: "Experience",
            work: "Work",
            blog: "Blog",
            more: "More",
            now: "Now",
            talks: "Talks",
            kevinBot: "kevin-bot",
            contact: "Contact",
          },

          /* === COMMON === */
          common: {
            scrollToTop: "Scroll to top",
            viewAll: "View all",
            readMore: "Read more",
            bookCall: "Book a call",
            loading: "Loading",
            notFound: "Not found",
            backHome: "Back home",
          },

          /* === THEME === */
          theme: {
            toggle: "Toggle theme",
            light: "Light",
            dark: "Dark",
            system: "System",
          },

          /* === LANG === */
          lang: {
            en: "EN",
            es: "ES",
          },

          /* === HOME === */
          home: {
            eyebrow: "SENIOR IC · AI PLATFORMS · FULL-STACK",
            headline: "Kevin Lopez.",
            subhead:
              "I ship production AI platforms and full-stack products where downtime costs money.",
            description:
              "Senior Software Engineer with 5+ years building production systems across fintech, ecommerce, and entertainment/AI. Currently shipping a multi-LLM chat platform for a major US entertainment client — owning the frontend, real-time streaming migrations, and the team's documentation portal.",
            statusBadge: "Available Q4 2026",
            primaryCta: "Read case studies",
            secondaryCta: "Try kevin-bot",
            tertiaryCta: "Get in touch",
            metrics: {
              years: { value: "5+", label: "YEARS SHIPPING PRODUCTION" },
              tickets: { value: "300+", label: "JIRA TICKETS SHIPPED" },
              prs: { value: "260+", label: "PRS MERGED ACROSS 10 REPOS" },
              loc: { value: "100k+", label: "LOC CHANGED IN 12 MONTHS" },
            },
            featuredWork: {
              eyebrow: "SELECTED WORK",
              headline: "What I've shipped recently",
              subhead_one:
                "{{count}} case study. Deep dives, architecture, and metrics from real production work.",
              subhead_other:
                "{{count}} case studies. Deep dives, architecture, and metrics from real production work.",
              viewAll: "View all work",
            },
            kevinBotTeaser: {
              eyebrow: "KEVIN-BOT · EMBEDDED RAG CHAT",
              headline: "A chatbot that answers about me, grounded in this very repo",
              subhead:
                "It answers only from my career-knowledge repository, and attributes each claim to the role or project it came from. Try asking about my Angular experience, the SSE migration, or my open-source plans.",
              primaryCta: "Try kevin-bot now",
              secondaryCta: "How it works →",
              tryAsking: "TRY ASKING",
              chips: {
                career: {
                  category: "CAREER",
                  question: "Walk me through your career arc from 2021 to today.",
                },
                aiPlatform: {
                  category: "AI PLATFORM",
                  question: "What was the SSE → WebSockets migration about?",
                },
                designSystems: {
                  category: "DESIGN SYSTEMS",
                  question: "Tell me about the EasySoft design system.",
                },
                process: {
                  category: "PROCESS",
                  question: "How do you keep 100% test coverage meaningful?",
                },
              },
            },
            botPreview: {
              title: "kevin-bot",
              subtitle: "ask my career-knowledge RAG · grounded, not generative",
              status: "ONLINE",
              qMeta: "EXAMPLE QUESTION",
              question: '"What was the SSE → WebSockets migration about?"',
              answer:
                "I led a production LLM chat migration at my current engagement. SSE was being buffered and timed out by corporate proxies, so answers stopped mid-sentence with no error anywhere. We moved to Socket.IO with JWT validated on the handshake and per-room namespaces. Proxy-timeout drops went to zero, and it unlocked bidirectional features SSE could not carry.",
              cite1: "Grounded in my career-knowledge repository",
              cite2: "Attributed by role and project, not by file path",
              hint: "Click anywhere · ask me anything",
            },
            blogTeaser: {
              eyebrow: "RECENT WRITING",
              headline: "From the blog",
              subhead: "Long-form on AI, scale, and engineering rigor.",
              viewAll: "View all posts",
              viewAllLong: "Read all posts",
            },
            nowTeaser: {
              eyebrow: "WHAT I'M WORKING ON NOW",
              headline: "Halfway through my second year at Globant.",
              body: "Shipping Claude, GPT, and Gemini integrations. Hardening the WebSockets streaming layer for high-latency corporate networks. Keeping the self-healing OpenAPI documentation pipeline in lockstep with reality. Keeping kevin-bot's corpus current with the work.",
              currentFocus: "CURRENT FOCUS",
              viewNow: "Read Now →",
              lastUpdated: "LAST UPDATED · 2026-07-12",
              items: {
                item1: "Shipping multi-LLM production chat (Claude, GPT, Gemini)",
                item2: "Owning the JedAI Portal and its PR-based editorial workflow",
                item3: "Hardening SSE → WS for high-latency corporate networks",
                item4: "Maintaining the self-healing OpenAPI documentation pipeline",
                item5: "Keeping kevin-bot's RAG corpus in sync with my work",
              },
            },
            contactCta: {
              eyebrow: "OPEN TO NEW ROLES",
              headline: "Let's talk",
              subline:
                "Senior IC role at the intersection of AI platforms and full-stack product engineering. Remote, LATAM-friendly, US-overlapping timezone (UTC-5).",
              primaryCta: "Book a call",
              email: "kevinjeff04@gmail.com",
            },
          },

          /* === ABOUT === */
          about: {
            eyebrow: "ABOUT",
            headline: "Five years of shipping systems where downtime costs money.",
            bio1: "Senior Software Engineer with 5+ years building production systems across fintech, ecommerce, and entertainment/AI. He currently ships a multi-LLM chat platform for a major US entertainment client — owning the frontend, leading the streaming-protocol migration, and building the team's documentation portal.",
            bio2: "His path moves deliberately between architectures: cross-platform Flutter, Python monorepos, AWS serverless microservices, and Angular fintech apps. A documented multiplier pattern runs through every role — clean-code reviews at his first job, 3 squad workshops at a LATAM fintech unicorn, design-system mentoring at a LATAM banking consultancy, and creator/owner of the documentation portal at his current engagement.",
            coreCompetencies: {
              title: "Five pillars I work across",
            },
            differentiators: {
              title: "What makes me distinctive",
              items: {
                multi_llm: {
                  title: "Multi-LLM production at enterprise scale",
                  description:
                    "Currently shipping Claude (Sonnet 4.6, Opus 4.6), GPT (4, 5.4), and Gemini (3.1, 3.5 Flash) into a single chat platform used daily by a major US entertainment client's cast members.",
                },
                proxy_streaming: {
                  title: "Proxy-safe LLM streaming",
                  description:
                    "Led the migration of a production LLM chat from SSE to WebSockets (Socket.IO), eliminating the proxy-timeout bug class and unlocking bidirectional features.",
                },
                design_system: {
                  title: "Design-system authorship at consulting scale",
                  description:
                    "Created a 70+ component Angular library (Atomic Design: atoms / molecules / organisms; hybrid PrimeNG + custom HTML/CSS) that was forked and sold to two banks.",
                },
                self_healing: {
                  title: "Self-healing platform engineering",
                  description:
                    "Shipped a CI/CD pipeline that keeps 600+ OpenAPI endpoints in sync with documentation automatically, and creator/owner of the team's docs portal (Astro/Starlight + headless CMS) so non-engineers can publish release notes via PR.",
                },
                multiplier: {
                  title: "Multiplier pattern across five roles",
                  description:
                    "Clean-code PR reviews at his first job, 3 workshops for my squad at a LATAM payments unicorn, informal design-system mentoring at a LATAM banking consultancy, and creator/owner of the documentation portal at my current engagement.",
                },
                bilingual: {
                  title: "Bilingual ES/EN, LATAM-based with US overlap",
                  description:
                    "Native Spanish, B2 English with 5+ years of professional use across US enterprise clients; 4-hour US-friendly overlap window from UTC-5.",
                },
              },
            },
            pillars: {
              architecture: { title: "Architecture" },
              backend: { title: "Backend" },
              frontend: { title: "Frontend" },
              devops: { title: "DevOps" },
              quality: { title: "Quality" },
            },
          },

          /* === EXPERIENCE === */
          experience: {
            eyebrow: "EXPERIENCE · 2021 – PRESENT",
            headline: "Five years of shipping systems at three different scales.",
            subhead:
              "Across fintech, ecommerce, and AI — easy-donate and Warby Parker (via IOET), full-stack at Kushki, senior at EasySoft (Angular, six LATAM banks), senior frontend at Disney (via Globant).",
          },

          /* === WORK DETAIL === */
          workDetail: {
            eyebrow: "CASE STUDY",
            meta: {
              role: "ROLE",
              period: "PERIOD",
              stack: "STACK",
              client: "CLIENT",
            },
            sections: {
              challenge: "Challenge",
              approach: "Approach",
              architecture: "Architecture",
            },
            diagram: {
              label: "ARCHITECTURE DIAGRAM PLACEHOLDER",
            },
            outcomes: {
              title: "Outcomes",
            },
            nav: {
              prev: "← PREVIOUS CASE STUDY",
              next: "NEXT CASE STUDY →",
            },
            notFound: {
              title: "Case study not found",
              body: "That case study does not exist (yet).",
              back: "← Back to work",
            },
          },

          blogPost: {
            authorBio:
              "Senior Software Engineer with 5+ years building production systems across fintech, ecommerce, and entertainment/AI. Currently shipping a multi-LLM chat platform at Globant/Disney.",
            tocLabel: "On this page",
            prevPost: "← Previous post",
            nextPost: "Next post →",
            coverPlaceholder: "[ COVER IMAGE PLACEHOLDER ]",
            notFound: {
              title: "Post not found",
              body: "That post does not exist (yet).",
              backToBlog: "← Back to blog",
            },
          },

          /* === WORK (index + detail) === */
          work: {
            eyebrow: "SELECTED WORK · 2021 – PRESENT",
            headline: "What I've shipped.",
            subhead:
              "Six case studies. Deep dives into architecture decisions, the metrics that came out, and the tradeoffs I made. Some client work is anonymized at the system level for IP reasons.",
          },

          /* === BLOG (index + post) === */
          blog: {
            eyebrow: "WRITING · 2026",
            headline: "Notes from production.",
            subhead:
              "What I learn shipping multi-LLM platforms, design systems, and senior-IC work at scale. Each post is grounded in real code, real production incidents, or real architecture decisions.",
            toc_label: "On this page",
            filtersLabel: "Filters",
            empty: "No posts match this filter yet.",
            filters: {
              all: "All",
              ai: "ai",
              rag: "rag",
              websockets: "websockets",
              "design-systems": "design-systems",
              angular: "angular",
              career: "career",
            },
            featured: {
              eyebrow: "FEATURED POST",
              cardEyebrow: "CASE-STUDY · 12 MIN READ",
              excerpt:
                "A RAG chatbot that answers recruiter questions about me, grounded in my career-knowledge repo, with citations on every claim. The full ingest + retrieval + defensive design story.",
              whyTitle: "WHY THIS POST",
              whyBody:
                "After enough recruiter screens, every conversation replays the same questions. This post walks through what I built, why source citations matter for defensive AI, and where kevin-bot is going next (observability, hybrid retrieval, moving off n8n).",
              cta: "Read post →",
              tags: ["#ai", "#rag", "#portfolio", "#llm"],
            },
            allPosts: {
              title: "All posts",
              count: "{{count}} POSTS",
            },
          },

          /* === KEVIN-BOT === */
          kevinBot: {
            eyebrow: "KEVIN-BOT · EMBEDDED RAG CHAT · LIVE",
            headline: "A chatbot that answers about me, grounded in this very repo.",
            subhead:
              "It answers only from my career-knowledge repository, with a public/private boundary enforced at ingest and at retrieval. Ask about the SSE migration, the design system, or what I'm shipping right now.",
            pipelineEyebrow: "HOW IT WORKS",
            pipelineTitle: "RAG pipeline in 4 steps",
            pipelineReadPost: "Read the full post",
            pipelineExpand: "Toggle pipeline details",
            pipeline: {
              ingest: {
                title: "INGEST",
                body: "An n8n workflow reads the repository from GitHub, drops anything marked private, chunks markdown by heading and YAML by top-level key, then embeds each chunk with gemini-embedding-001 (3072 dim).",
              },
              store: {
                title: "STORE",
                body: "Every run builds into a scratch table in Postgres + pgvector, verifies the counts, then promotes with an atomic rename — the previous corpus is kept for rollback.",
              },
              retrieve: {
                title: "RETRIEVE",
                body: "Query → embed → cosine similarity, topK=6, filtered to chunks marked public. No reranker: at this corpus size a sequential scan wins.",
              },
              generate: {
                title: "GENERATE",
                body: "The agent runs on OpenRouter and streams through a hardened serverless proxy — rate limited, reasoning tags stripped. Claims are attributed to the role or project, not to a file path.",
              },
            },
            panel: {
              title: "kevin-bot",
              subline: "GROUNDED IN MY CAREER RECORD",
              live: "LIVE",
              clear: "Clear chat",
              stop: "Stop generating",
              placeholder: "Ask kevin-bot anything…",
              send: "Send",
              hintKeys: "ENTER TO SEND · SHIFT+ENTER FOR A NEW LINE",
              hintLang: "ANSWERS IN ES / EN",
              welcome:
                "Hi — I'm kevin-bot. I only answer from Kevin's career-knowledge repository, and I'll tell you which role or project each claim comes from.",
            },
            scope: {
              title: "What this bot can and can't answer",
              body: "Only what's written in my career-knowledge repository: roles, projects, skills, and the decisions behind them. Compensation and client-internal identifiers are out of reach by design — filtered at ingest and again at retrieval.",
              link: "HOW THE BOUNDARY WORKS →",
            },
            quickRepliesLabel: "TRY ASKING",
            segments: {
              recruiter: "Recruiter",
              engineer: "Engineer",
              client: "Client",
            },
            quickReplies: {
              recruiter: [
                "Are you open to senior IC roles?",
                "Availability and timezone overlap",
                "Career arc 2021 → today",
              ],
              engineer: [
                "What was the SSE → WebSockets migration about?",
                "How does the self-healing OpenAPI pipeline work?",
                "How do you keep 100% test coverage meaningful?",
              ],
              client: [
                "What did you ship at your current engagement?",
                "Tell me about the Easysoft design system and who bought it.",
                "Describe your multiplier pattern across 5 roles.",
              ],
            },
            suggestedNext: "SUGGESTED NEXT",
            actions: {
              copy: "Copy answer",
              copied: "Copied",
              regenerate: "Regenerate answer",
              elapsed: "{{seconds}} s",
            },
            error: {
              title: "Couldn't reach kevin-bot",
              body: "The request timed out. Your question is still in the box — nothing was lost.",
              retry: "Try again",
            },
          },

          /* === NOW === */
          now: {
            eyebrow: "NOW · JULY 2026",
            headline: "Now.",
            subhead1:
              "I'm halfway through my second year at Globant, leading frontend on a major entertainment client's multi-LLM chat platform that cast members use every day. This quarter is dominated by multi-LLM production work: shipping new Claude, GPT, and Gemini integrations; hardening the WebSockets streaming layer for high-latency corporate networks; and keeping the self-healing OpenAPI documentation pipeline in lockstep with reality.",
            subhead2:
              "Outside work I keep investing in kevinjlope.dev and the embedded kevin-bot, so the public surface matches the work I'm actually shipping — including the public/private boundary that decides what the bot is allowed to retrieve.",
            lastUpdated: "LAST UPDATED · 2026-07-12",
            activeLabel: "Current focus",
            exploringLabel: "Exploring",
            active: {
              item1:
                "Shipping multi-LLM production chat (Claude Sonnet 4.6, Opus 4.6, GPT-4, GPT-5.4, Gemini 3.1/3.5) into cast members' hands every week",
              item2:
                "Owning the JedAI Portal — Astro/Starlight plus a headless CMS so non-engineers publish release notes through pull requests",
              item3:
                "Hardening the SSE → WebSockets streaming layer for high-latency corporate networks (timeouts, ping tuning, JWT auth on upgrade)",
              item4:
                "Maintaining the self-healing OpenAPI documentation pipeline that keeps 600+ endpoints in sync with the docs portal automatically",
              item5:
                "Working across both frontend generations — Angular 18 on v1 and Svelte/SvelteKit on v2",
              item6:
                "Keeping kevin-bot's RAG corpus current so the public chatbot reflects what I'm actually shipping",
            },
            exploring: {
              item1:
                "Drafting a generalised, employer-agnostic version of the self-healing OpenAPI documentation pipeline for open-source release",
              item2:
                "Quietly scoping my next senior IC role at the intersection of AI platforms and full-stack product engineering — remote, LATAM-friendly, with US timezone overlap",
              item3:
                "Building an eval set for kevin-bot — expected answers plus leak probes, run in CI, so a prompt change cannot quietly weaken the privacy boundary",
            },
            archiveLink: "Previous quarters (archive)",
          },

          /* === TALKS === */
          talks: {
            eyebrow: "TALKS · PUBLICATIONS · AWARDS · OPEN SOURCE",
            headline: "Speaking, writing, and building in public.",
            subhead:
              "A consolidated list of conference talks, publications, awards, and open-source work. Some categories are work-in-progress — I'm being honest about what's public and what's not yet.",
            speaking: {
              title: "Speaking",
              subtitle: "Conference talks, internal training sessions, workshops",
              count: "1 ITEM",
              group: "INTERNAL TRAINING · 2023",
              itemTitle: "Kushki Engineering Workshops — Clean Code, TDD, Clean Architecture, DDD",
              meta: "Kushki Pagos · internal training · 6 attendees",
              takeaway:
                "Three 1h sessions for the squad (slides + hands-on) on Clean Code, Unit/Integration Testing, TDD, Clean Architecture, MongoDB Triggers, Feature Toggles, and DDD. Origin: a teammate's daily request — \"Kevin, help us understand what you're saying.\" Sources: Robert C. Martin, Eric Evans, Vaughn Vernon, Kent Beck.",
            },
            publications: {
              title: "Publications",
              subtitle: "Long-form writing on engineering and AI",
              count: "0 ITEMS",
              empty: {
                cta: "ZERO ITEMS YET · BLOG IS THE SURFACE",
                body: "No public items yet — being curated. The blog on kevinjlope.dev is the active publication surface (RSS feed available), and longer-form pieces are in the pipeline for 2026.",
              },
            },
            awards: {
              title: "Awards",
              subtitle: "External recognition and formal awards",
              count: "0 ITEMS",
              empty: {
                cta: "NO FORMAL AWARDS · SCOPE-BASED RECOGNITION IS IN /WORK",
                body: "No formal external awards documented on LinkedIn, in internal records, or in team-lead check-ins (verified 2026-07). Scope-based recognition per role is captured in the case studies on /work: SSE → WS migration, Open WebUI upgrade, 2-bank design system sale, 3 Kushki workshops.",
              },
            },
            openSource: {
              title: "Open source",
              subtitle: "Public packages, repositories, and engineering patterns",
              count: "0 ITEMS",
              empty: {
                cta: "PLANNED · PENDING EMPLOYER SIGN-OFF",
                body: "Planned OSS portfolio (pending employer sign-off): a generalised version of the self-healing OpenAPI documentation pipeline, a generic Astro/Starlight + headless CMS docs portal template, and AI-platform engineering patterns extracted into standalone repos.",
              },
            },
          },

          /* === CONTACT === */
          contact: {
            eyebrow: "OPEN TO SENIOR IC ROLES · UTC-5 · LATAM",
            title: "Get in Touch",
            headline: "Let's talk.",
            subhead:
              "Senior IC role at the intersection of AI platforms and full-stack product engineering. Remote, LATAM-friendly, US-overlapping timezone. Or just talk shop about RAG pipelines, design systems, or Angular major migrations.",
            hook: "Senior IC role at the intersection of AI platforms and full-stack product engineering. Remote, LATAM-friendly, US-overlapping timezone. Or just talk shop about RAG pipelines, design systems, or Angular major migrations.",
            cta: "Book a 30-min call",
            email: "kevinjeff04@gmail.com",
            calendlyBadge: "CALENDLY EMBED PLACEHOLDER",
            calendlyHeadline: "Pick a time that works for you",
            calendlyHint: "30-min intro call · UTC-5 (Guayaquil) · async-friendly",
            calendlyPlaceholder: "[ react-calendly InlineWidget goes here ]",
            faqEyebrow: "QUICK ANSWERS",
            faqTitle: "Things recruiters ask first",
            faqLabel: "FAQ",
            faq: {
              q1: {
                q: "Are you open to senior IC roles right now?",
                a: "Yes. Remote, LATAM-friendly, US-overlapping timezone (UTC-5). Targeting AI-platform or senior full-stack roles — see /about for the full differentiators and /work for case studies. Compensation is a conversation I have by email.",
              },
              q2: {
                q: "Where are you based?",
                a: "Guayaquil, Ecuador. Native Spanish, B2 English with 5+ years of professional use across US enterprise clients. Daily US overlap is 8am-12pm EST.",
              },
              q3: {
                q: "What's your notice period / earliest start?",
                a: "Around 4 weeks. I can overlap during the transition for the right role.",
              },
            },
            socialsEyebrow: "OR FIND ME ON",
            socials: {
              github: {
                label: "GitHub",
                handle: "@kevinjlope",
                href: "https://github.com/kevinjlope",
              },
              linkedin: {
                label: "LinkedIn",
                handle: "in/kevinjlope",
                href: "https://linkedin.com/in/kevinjlope",
              },
              twitter: {
                label: "Twitter",
                handle: "@kevinjlope",
                href: "https://twitter.com/kevinjlope",
              },
            },
          },

          /* === LEGACY KEYS (preserved for v1 components still in flight) === */
          settings: {
            title: "Settings",
            language: "Language",
            theme: "Theme",
            dark_mode: "Dark Mode",
            theme_dark: "Dark",
            theme_light: "Light",
            sound: "Audio",
            sound_on: "Sound on",
            sound_off: "Sound off",
            on: "On",
            off: "Off",
            font_size: "Font Size",
            font_size_small: "Small",
            font_size_default: "Default",
            font_size_large: "Large",
            aria_open: "Open Settings",
            aria_close: "Close Settings",
          },
          ai: {
            status: "Neural Agent Active",
            scanning: "Thinking…",
          },
          notFound: {
            title: "Route not found",
            body: "That page does not exist (yet).",
          },
        },
      },
      es: {
        translation: {
          /* === NAV === */
          nav: {
            home: "Inicio",
            about: "Sobre Mí",
            experience: "Experiencia",
            work: "Trabajo",
            blog: "Blog",
            more: "Más",
            now: "Ahora",
            talks: "Charlas",
            kevinBot: "kevin-bot",
            contact: "Contacto",
          },

          /* === COMMON === */
          common: {
            scrollToTop: "Volver arriba",
            viewAll: "Ver todo",
            readMore: "Leer más",
            bookCall: "Agendar llamada",
            loading: "Cargando",
            notFound: "No encontrado",
            backHome: "Volver al inicio",
          },

          /* === THEME === */
          theme: {
            toggle: "Cambiar tema",
            light: "Claro",
            dark: "Oscuro",
            system: "Sistema",
          },

          /* === LANG === */
          lang: {
            en: "EN",
            es: "ES",
          },

          /* === HOME === */
          home: {
            eyebrow: "IC SENIOR · PLATAFORMAS IA · FULL-STACK",
            headline: "Kevin Lopez.",
            subhead:
              "Llevo a producción plataformas de IA y productos full-stack donde el downtime cuesta dinero.",
            description:
              "Ingeniero de Software Senior con 5+ años construyendo sistemas en producción en fintech, ecommerce y entretenimiento/IA. Actualmente desarrollo una plataforma de chat multi-LLM para un gran cliente de entretenimiento de EE. UU. — responsable del frontend, de las migraciones de streaming en tiempo real y del portal de documentación del equipo.",
            statusBadge: "Disponible Q4 2026",
            primaryCta: "Leer casos de estudio",
            secondaryCta: "Probar kevin-bot",
            tertiaryCta: "Ponte en contacto",
            metrics: {
              years: { value: "5+", label: "AÑOS LLEVANDO A PRODUCCIÓN" },
              tickets: { value: "300+", label: "TICKETS DE JIRA ENTREGADOS" },
              prs: { value: "260+", label: "PRS MERGEADOS EN 10 REPOS" },
              loc: { value: "100k+", label: "LOC CAMBIADAS EN 12 MESES" },
            },
            featuredWork: {
              eyebrow: "TRABAJO SELECCIONADO",
              headline: "Lo que he entregado recientemente",
              subhead_one:
                "{{count}} caso de estudio. Arquitectura, métricas y decisiones reales de producción.",
              subhead_other:
                "{{count}} casos de estudio. Arquitectura, métricas y decisiones reales de producción.",
              viewAll: "Ver todo",
            },
            kevinBotTeaser: {
              eyebrow: "KEVIN-BOT · CHAT RAG INTEGRADO",
              headline: "Un chatbot que responde sobre mí, fundamentado en este mismo repo",
              subhead:
                "Responde únicamente desde mi repositorio career-knowledge y atribuye cada afirmación al rol o proyecto del que salió. Prueba a preguntar sobre mi experiencia con Angular, la migración SSE o mis planes open-source.",
              primaryCta: "Probar kevin-bot ahora",
              secondaryCta: "Cómo funciona →",
              tryAsking: "PRUEBA A PREGUNTAR",
              chips: {
                career: {
                  category: "CARRERA",
                  question: "Guíame por tu trayectoria profesional de 2021 a hoy.",
                },
                aiPlatform: {
                  category: "PLATAFORMA IA",
                  question: "¿De qué trataba la migración de SSE → WebSockets?",
                },
                designSystems: {
                  category: "DESIGN SYSTEMS",
                  question: "Cuéntame sobre el design system de EasySoft.",
                },
                process: {
                  category: "PROCESO",
                  question: "¿Cómo logras que un 100% de cobertura siga siendo útil?",
                },
              },
            },
            botPreview: {
              title: "kevin-bot",
              subtitle: "pregúntale a mi RAG de career-knowledge · cita fuentes",
              status: "EN LÍNEA",
              qMeta: "PREGUNTA DE EJEMPLO",
              question: '"¿De qué trataba la migración de SSE → WebSockets?"',
              answer:
                "Lideré la migración de un chat LLM en producción en mi cliente actual. SSE moría detrás de los proxies corporativos tras ~20s de inactividad. Migramos a Socket.IO con auth JWT en el handshake + namespaces por sala. Los errores de proxy-timeout cayeron a cero, y el cambio desbloqueó features bidireccionales como cancelar a mitad de stream y subir archivos durante el streaming.",
              cite1: "article-digest.md · A2",
              cite2: "experience/01-.../README.md",
              hint: "Haz clic donde sea · pregúntame lo que quieras",
            },
            blogTeaser: {
              eyebrow: "ESCRITURA RECIENTE",
              headline: "Desde el blog",
              subhead: "Formato largo sobre IA, escala y rigor de ingeniería.",
              viewAll: "Ver todos los posts",
              viewAllLong: "Leer todos los posts",
            },
            nowTeaser: {
              eyebrow: "EN LO QUE ESTOY TRABAJANDO AHORA",
              headline: "A mitad de mi segundo año en Globant.",
              body: "Llevando a producción integraciones de Claude, GPT y Gemini. Endureciendo la capa de streaming WebSockets para redes corporativas de alta latencia. Manteniendo el pipeline de documentación OpenAPI auto-reparable al día con la realidad. Manteniendo el corpus de kevin-bot al día con el trabajo.",
              currentFocus: "FOCO ACTUAL",
              viewNow: "Leer Now →",
              lastUpdated: "ÚLTIMA ACTUALIZACIÓN · 2026-07-12",
              items: {
                item1: "Llevando a producción chat multi-LLM (Claude, GPT, Gemini)",
                item2: "A cargo del JedAI Portal y su flujo editorial basado en pull requests",
                item3: "Endureciendo SSE → WS para redes corporativas de alta latencia",
                item4: "Manteniendo el pipeline de documentación OpenAPI auto-reparable",
                item5: "Manteniendo el corpus RAG de kevin-bot en sync con mi trabajo",
              },
            },
            contactCta: {
              eyebrow: "DISPONIBLE PARA NUEVOS ROLES",
              headline: "Hablemos",
              subline:
                "Rol IC senior en la intersección de plataformas IA e ingeniería de producto full-stack. Remoto, friendly con LATAM, timezone solapado con EE. UU. (UTC-5).",
              primaryCta: "Agenda una llamada",
              email: "kevinjeff04@gmail.com",
            },
          },

          /* === ABOUT === */
          about: {
            eyebrow: "SOBRE MÍ",
            headline: "Cinco años lanzando sistemas donde el downtime cuesta dinero.",
            bio1: "Ingeniero de Software Senior con más de 5 años construyendo sistemas de producción en fintech, ecommerce y entretenimiento/IA. Actualmente desarrolla una plataforma de chat multi-LLM para un importante cliente de entretenimiento de EE. UU. — liderando el frontend, la migración del protocolo de streaming y el portal de documentación del equipo.",
            bio2: "Su trayectoria se mueve deliberadamente entre arquitecturas: Flutter multiplataforma, monorepos Python, microservicios serverless en AWS y apps fintech en Angular. Un patrón multiplicador documentado atraviesa cada rol — revisiones de clean code en su primer trabajo, 3 workshops para su squad en un unicornio fintech latinoamericano, mentoría de design system en una consultora bancaria latinoamericana y creador/propietario del portal de documentación en su actual proyecto.",
            coreCompetencies: {
              title: "Cinco pilares en los que trabajo",
            },
            differentiators: {
              title: "Lo que me hace distintivo",
              items: {
                multi_llm: {
                  title: "Producción multi-LLM a escala empresarial",
                  description:
                    "Actualmente integra Claude (Sonnet 4.6, Opus 4.6), GPT (4, 5.4) y Gemini (3.1, 3.5 Flash) en una sola plataforma de chat usada a diario por el elenco de un importante cliente de entretenimiento de EE. UU.",
                },
                proxy_streaming: {
                  title: "Streaming de LLM seguro ante proxies",
                  description:
                    "Lideró la migración de un chat con LLM en producción de SSE a WebSockets (Socket.IO), eliminando la clase de errores por timeout de proxy y habilitando funcionalidades bidireccionales.",
                },
                design_system: {
                  title: "Autoría de design system a escala de consultoría",
                  description:
                    "Creó una librería Angular de más de 70 componentes (Atomic Design: átomos / moléculas / organismos; híbrido PrimeNG + HTML/CSS custom) que se bifurcó y vendió a dos bancos.",
                },
                self_healing: {
                  title: "Ingeniería de plataformas auto-recuperables",
                  description:
                    "Entregó un pipeline de CI/CD que mantiene más de 600 endpoints OpenAPI sincronizados con la documentación automáticamente, y es creador/propietario del portal de docs del equipo (Astro/Starlight + headless CMS) para que personas no técnicas publiquen release notes vía PR.",
                },
                multiplier: {
                  title: "Patrón multiplicador a lo largo de cinco roles",
                  description:
                    "Revisiones de PR de clean code en su primer trabajo, 3 workshops para su squad en un unicornio de pagos latinoamericano, mentoría informal de design system en una consultora bancaria latinoamericana y creador/propietario del portal de documentación en su actual proyecto.",
                },
                bilingual: {
                  title: "Bilingüe ES/EN, base LATAM con overlap a EE. UU.",
                  description:
                    "Español nativo, inglés B2 con más de 5 años de uso profesional con clientes empresariales de EE. UU.; ventana de overlap de 4 h amigable con EE. UU. desde UTC-5.",
                },
              },
            },
            pillars: {
              architecture: { title: "Arquitectura" },
              backend: { title: "Backend" },
              frontend: { title: "Frontend" },
              devops: { title: "DevOps" },
              quality: { title: "Calidad" },
            },
          },

          /* === EXPERIENCE === */
          experience: {
            eyebrow: "EXPERIENCIA · 2021 – PRESENTE",
            headline: "Cinco años lanzando sistemas a tres escalas distintas.",
            subhead:
              "A través de fintech, ecommerce e IA — easy-donate y Warby Parker (vía IOET), full-stack en Kushki, senior en EasySoft (Angular, seis bancos de LATAM), frontend senior en Disney (vía Globant).",
          },

          /* === WORK === */
          work: {
            eyebrow: "TRABAJO SELECCIONADO · 2021 – PRESENTE",
            headline: "Lo que he lanzado.",
            subhead:
              "Seis casos de estudio. Análisis a fondo de decisiones de arquitectura, las métricas que produjeron y los tradeoffs que hice. Parte del trabajo con clientes está anonimizado a nivel de sistema por razones de IP.",
          },

          /* === WORK DETAIL === */
          workDetail: {
            eyebrow: "CASO DE ESTUDIO",
            meta: {
              role: "ROL",
              period: "PERIODO",
              stack: "STACK",
              client: "CLIENTE",
            },
            sections: {
              challenge: "El reto",
              approach: "El enfoque",
              architecture: "La arquitectura",
            },
            diagram: {
              label: "PLACEHOLDER DE DIAGRAMA DE ARQUITECTURA",
            },
            outcomes: {
              title: "Resultados",
            },
            nav: {
              prev: "← CASO DE ESTUDIO ANTERIOR",
              next: "SIGUIENTE CASO DE ESTUDIO →",
            },
            notFound: {
              title: "Caso de estudio no encontrado",
              body: "Ese caso de estudio no existe (todavía).",
              back: "← Volver a trabajo",
            },
          },

          blogPost: {
            authorBio:
              "Senior Software Engineer con más de 5 años construyendo sistemas productivos en fintech, ecommerce y entretenimiento/IA. Actualmente desarrollando una plataforma de chat multi-LLM en Globant/Disney.",
            tocLabel: "En esta página",
            prevPost: "← Publicación anterior",
            nextPost: "Publicación siguiente →",
            coverPlaceholder: "[ IMAGEN DE PORTADA ]",
            notFound: {
              title: "Post no encontrado",
              body: "Ese post no existe (todavía).",
              backToBlog: "← Volver al blog",
            },
          },

          /* === BLOG (index + post) === */
          blog: {
            eyebrow: "ESCRITOS · 2026",
            headline: "Notas desde producción.",
            subhead:
              "Lo que aprendo construyendo plataformas multi-LLM, design systems y trabajo de senior IC a escala. Cada post se basa en código real, incidentes reales o decisiones de arquitectura reales.",
            toc_label: "En esta página",
            filtersLabel: "Filtros",
            empty: "Ningún post coincide con este filtro todavía.",
            filters: {
              all: "Todo",
              ai: "ai",
              rag: "rag",
              websockets: "websockets",
              "design-systems": "design-systems",
              angular: "angular",
              career: "career",
            },
            featured: {
              eyebrow: "POST DESTACADO",
              cardEyebrow: "CASO DE ESTUDIO · 12 MIN DE LECTURA",
              excerpt:
                "Un chatbot RAG que responde preguntas de recruiters sobre mí, basado en mi repo de conocimiento profesional, con citas en cada afirmación. La historia completa de ingest + retrieval + diseño defensivo.",
              whyTitle: "POR QUÉ ESTE POST",
              whyBody:
                "Después de tantas entrevistas con recruiters, cada conversación repite las mismas preguntas. Este post recorre qué construí, por qué las citas de fuente importan para IA defensiva, y hacia dónde va kevin-bot (observabilidad, retrieval híbrido, salir de n8n).",
              cta: "Leer post →",
              tags: ["#ai", "#rag", "#portfolio", "#llm"],
            },
            allPosts: {
              title: "Todos los posts",
              count: "{{count}} POSTS",
            },
          },

          /* === KEVIN-BOT === */
          kevinBot: {
            eyebrow: "KEVIN-BOT · CHAT RAG INTEGRADO · EN VIVO",
            headline: "Un chatbot que responde sobre mí, basado en este mismo repositorio.",
            subhead:
              "Responde únicamente desde mi repositorio de conocimiento profesional, con una frontera público/privado aplicada en la ingesta y en el retrieval. Pregunta por la migración SSE, el design system o lo que estoy construyendo ahora.",
            pipelineEyebrow: "CÓMO FUNCIONA",
            pipelineTitle: "Pipeline RAG en 4 pasos",
            pipelineReadPost: "Leer el post completo",
            pipelineExpand: "Ver el detalle del pipeline",
            pipeline: {
              ingest: {
                title: "INGESTA",
                body: "Un workflow de n8n lee el repositorio desde GitHub, descarta todo lo marcado como privado, trocea el markdown por encabezado y el YAML por clave de primer nivel, y genera embeddings con gemini-embedding-001 (3072 dim).",
              },
              store: {
                title: "ALMACENAMIENTO",
                body: "Cada run construye en una tabla scratch en Postgres + pgvector, verifica los conteos y recién entonces promueve con un rename atómico — el corpus anterior se conserva para rollback.",
              },
              retrieve: {
                title: "RECUPERACIÓN",
                body: "Consulta → embedding → similitud coseno, topK=6, filtrado a los chunks marcados como públicos. Sin reranker: a este tamaño de corpus gana el scan secuencial.",
              },
              generate: {
                title: "GENERACIÓN",
                body: "El agente corre sobre OpenRouter y va en stream por un proxy serverless endurecido — con rate limit y filtrado de las etiquetas de razonamiento. Cada afirmación se atribuye al rol o proyecto, no a una ruta de archivo.",
              },
            },
            panel: {
              title: "kevin-bot",
              subline: "BASADO EN MI REGISTRO PROFESIONAL",
              live: "EN VIVO",
              clear: "Limpiar chat",
              stop: "Detener generación",
              placeholder: "Pregúntale lo que sea a kevin-bot…",
              send: "Enviar",
              hintKeys: "ENTER PARA ENVIAR · SHIFT+ENTER PARA SALTO DE LÍNEA",
              hintLang: "RESPONDE EN ES / EN",
              welcome:
                "Hola — soy kevin-bot. Respondo únicamente desde el repositorio de conocimiento profesional de Kevin, y te digo de qué rol o proyecto sale cada afirmación.",
            },
            scope: {
              title: "Qué puede y qué no puede responder este bot",
              body: "Solo lo que está escrito en mi repositorio de conocimiento profesional: roles, proyectos, habilidades y las decisiones detrás de ellos. La compensación y los identificadores internos de cliente están fuera de alcance por diseño — filtrados en la ingesta y de nuevo en el retrieval.",
              link: "CÓMO FUNCIONA LA FRONTERA →",
            },
            quickRepliesLabel: "PRUEBA PREGUNTANDO",
            segments: {
              recruiter: "Reclutador",
              engineer: "Ingeniero",
              client: "Cliente",
            },
            quickReplies: {
              recruiter: [
                "¿Estás abierto a roles senior IC?",
                "Disponibilidad y solapamiento de zona horaria",
                "Trayectoria 2021 → hoy",
              ],
              engineer: [
                "¿De qué trata la migración SSE → WebSockets?",
                "¿Cómo funciona el pipeline OpenAPI auto-curativo?",
                "¿Cómo mantienes significativa una cobertura del 100%?",
              ],
              client: [
                "¿Qué entregaste en tu compromiso actual?",
                "Cuéntame sobre el design system de Easysoft y quién lo compró.",
                "Describe tu patrón de multiplicador en 5 roles.",
              ],
            },
            suggestedNext: "SUGERIDAS A CONTINUACIÓN",
            actions: {
              copy: "Copiar respuesta",
              copied: "Copiado",
              regenerate: "Regenerar respuesta",
              elapsed: "{{seconds}} s",
            },
            error: {
              title: "No se pudo contactar a kevin-bot",
              body: "La solicitud expiró. Tu pregunta sigue en la caja — no se perdió nada.",
              retry: "Intentar de nuevo",
            },
          },

          /* === NOW === */
          now: {
            eyebrow: "AHORA · JULIO 2026",
            headline: "Ahora.",
            subhead1:
              "Estoy a mitad de mi segundo año en Globant, liderando el frontend de la plataforma de chat multi-LLM de un importante cliente de entretenimiento que los miembros del elenco usan todos los días. Este trimestre está dominado por trabajo de producción multi-LLM: enviando nuevas integraciones de Claude, GPT y Gemini; endureciendo la capa de streaming de WebSockets para redes corporativas de alta latencia; y manteniendo el pipeline de documentación OpenAPI autorreparable en sincronía con la realidad.",
            subhead2:
              "Fuera del trabajo sigo invirtiendo en kevinjlope.dev y en el kevin-bot integrado, para que la superficie pública refleje el trabajo que realmente estoy entregando — incluida la frontera público/privado que decide qué puede recuperar el bot.",
            lastUpdated: "ÚLTIMA ACTUALIZACIÓN · 2026-07-12",
            activeLabel: "Enfoque actual",
            exploringLabel: "Explorando",
            active: {
              item1:
                "Llevando a producción chat multi-LLM (Claude Sonnet 4.6, Opus 4.6, GPT-4, GPT-5.4, Gemini 3.1/3.5) a las manos de los miembros del elenco cada semana",
              item2:
                "A cargo del JedAI Portal — Astro/Starlight más un headless CMS para que gente no técnica publique release notes vía pull requests",
              item3:
                "Endureciendo la capa de streaming SSE → WebSockets para redes corporativas de alta latencia (timeouts, ajuste de ping, autenticación JWT en el upgrade)",
              item4:
                "Manteniendo el pipeline de documentación OpenAPI autorreparable que mantiene 600+ endpoints sincronizados con el portal de docs automáticamente",
              item5:
                "Trabajando en las dos generaciones del frontend — Angular 18 en v1 y Svelte/SvelteKit en v2",
              item6:
                "Manteniendo actualizado el corpus RAG de kevin-bot para que el chatbot público refleje lo que realmente estoy entregando",
            },
            exploring: {
              item1:
                "Esbozando una versión generalizada y agnóstica al empleador del pipeline de documentación OpenAPI autorreparable para liberarlo como open source",
              item2:
                "Explorando en silencio mi próximo rol como IC senior en la intersección entre plataformas de IA e ingeniería de producto full-stack — remoto, friendly para LATAM, con solapamiento de horario con EE. UU.",
              item3:
                "Construyendo un set de evaluación para kevin-bot — respuestas esperadas más sondas de fuga, corriendo en CI, para que un cambio de prompt no debilite en silencio la frontera de privacidad",
            },
            archiveLink: "Trimestres anteriores (archivo)",
          },

          /* === TALKS === */
          talks: {
            eyebrow: "CHARLAS · PUBLICACIONES · RECONOCIMIENTOS · OPEN SOURCE",
            headline: "Charlar, escribir y construir en público.",
            subhead:
              "Lista consolidada de charlas, publicaciones, reconocimientos y trabajo open source. Algunas categorías están en progreso — soy directo sobre lo que es público y lo que aún no lo es.",
            speaking: {
              title: "Charlas",
              subtitle: "Charlas en conferencias, sesiones de entrenamiento interno, talleres",
              count: "1 ELEMENTO",
              group: "ENTRENAMIENTO INTERNO · 2023",
              itemTitle: "Kushki Engineering Workshops — Clean Code, TDD, Clean Architecture, DDD",
              meta: "Kushki Pagos · entrenamiento interno · 6 asistentes",
              takeaway:
                'Tres sesiones de 1h para el squad (slides + práctica) sobre Clean Code, Unit/Integration Testing, TDD, Clean Architecture, MongoDB Triggers, Feature Toggles y DDD. Origen: una petición diaria de un compañero — "Kevin, ayúdanos a entender lo que dices." Fuentes: Robert C. Martin, Eric Evans, Vaughn Vernon, Kent Beck.',
            },
            publications: {
              title: "Publicaciones",
              subtitle: "Escritos largos sobre ingeniería e IA",
              count: "0 ELEMENTOS",
              empty: {
                cta: "CERO ELEMENTOS AÚN · EL BLOG ES LA SUPERFICIE",
                body: "Aún no hay elementos públicos — están siendo curados. El blog en kevinjlope.dev es la superficie activa de publicación (feed RSS disponible), y piezas más largas están en el pipeline para 2026.",
              },
            },
            awards: {
              title: "Reconocimientos",
              subtitle: "Reconocimiento externo y premios formales",
              count: "0 ELEMENTOS",
              empty: {
                cta: "SIN PREMIOS FORMALES · EL RECONOCIMIENTO POR ALCANCE ESTÁ EN /WORK",
                body: "Sin premios externos formales documentados en LinkedIn, registros internos o check-ins con team leads (verificado 2026-07). El reconocimiento basado en alcance por rol se captura en los case studies de /work: migración SSE → WebSockets, upgrade de Open WebUI, venta del design system a 2 bancos, 3 talleres en Kushki.",
              },
            },
            openSource: {
              title: "Open source",
              subtitle: "Paquetes públicos, repositorios y patrones de ingeniería",
              count: "0 ELEMENTOS",
              empty: {
                cta: "PLANIFICADO · PENDIENTE DE APROBACIÓN DEL EMPLEADOR",
                body: "Portfolio OSS planificado (pendiente de aprobación del empleador): una versión generalizada del pipeline auto-curativo de documentación OpenAPI, una plantilla genérica de portal de docs Astro/Starlight + headless CMS, y patrones de ingeniería de plataformas de IA extraídos a repositorios standalone.",
              },
            },
          },

          /* === CONTACT === */
          contact: {
            eyebrow: "ABIERTO A ROLES SENIOR IC · UTC-5 · LATAM",
            title: "Contacto",
            headline: "Hablemos.",
            subhead:
              "Rol senior IC en la intersección de plataformas de IA e ingeniería de producto full-stack. Remoto, friendly para LATAM, timezone con superposición a EE.UU. O simplemente hablemos de RAG pipelines, design systems o migraciones mayores de Angular.",
            hook: "Rol senior IC en la intersección de plataformas de IA e ingeniería de producto full-stack. Remoto, friendly para LATAM, timezone con superposición a EE.UU. O simplemente hablemos de RAG pipelines, design systems o migraciones mayores de Angular.",
            cta: "Agendar llamada de 30 min",
            email: "kevinjeff04@gmail.com",
            calendlyBadge: "CALENDLY EMBED PLACEHOLDER",
            calendlyHeadline: "Escoge un horario que te funcione",
            calendlyHint: "Llamada intro de 30 min · UTC-5 (Guayaquil) · async-friendly",
            calendlyPlaceholder: "[ react-calendly InlineWidget va aquí ]",
            faqEyebrow: "RESPUESTAS RÁPIDAS",
            faqTitle: "Lo primero que preguntan los reclutadores",
            faqLabel: "FAQ",
            faq: {
              q1: {
                q: "¿Estás abierto a roles senior IC ahora mismo?",
                a: "Sí. Remoto, friendly para LATAM, timezone con superposición a EE.UU. (UTC-5). Busco roles en plataformas de IA o senior full-stack — ve /about para los diferenciadores completos y /work para case studies. La compensación la converso por correo.",
              },
              q2: {
                q: "¿Dónde estás ubicado?",
                a: "Guayaquil, Ecuador. Español nativo, inglés B2 con 5+ años de uso profesional con clientes enterprise de EE.UU. La superposición diaria con EE.UU. es 8am-12pm EST.",
              },
              q3: {
                q: "¿Cuál es tu periodo de preaviso / inicio más temprano?",
                a: "Alrededor de 4 semanas. Puedo superponer durante la transición para el rol correcto.",
              },
            },
            socialsEyebrow: "O ENCÚNTRAME EN",
            socials: {
              github: {
                label: "GitHub",
                handle: "@kevinjlope",
                href: "https://github.com/kevinjlope",
              },
              linkedin: {
                label: "LinkedIn",
                handle: "in/kevinjlope",
                href: "https://linkedin.com/in/kevinjlope",
              },
              twitter: {
                label: "Twitter",
                handle: "@kevinjlope",
                href: "https://twitter.com/kevinjlope",
              },
            },
          },

          /* === LEGACY KEYS === */
          settings: {
            title: "Ajustes",
            language: "Idioma",
            theme: "Tema",
            dark_mode: "Modo Oscuro",
            theme_dark: "Oscuro",
            theme_light: "Claro",
            sound: "Audio",
            sound_on: "Sonido activado",
            sound_off: "Sonido desactivado",
            on: "Activado",
            off: "Desactivado",
            font_size: "Tamaño de Fuente",
            font_size_small: "Pequeño",
            font_size_default: "Normal",
            font_size_large: "Grande",
            aria_open: "Abrir Ajustes",
            aria_close: "Cerrar Ajustes",
          },
          ai: {
            status: "Agente Neural Activo",
            scanning: "Pensando…",
          },
          notFound: {
            title: "Ruta no encontrada",
            body: "Esa página no existe (todavía).",
          },
        },
      },
    },
  });

export default i18n;
